import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Lightning } from "@phosphor-icons/react";

export default function MaintenancePlans() {
    const { user } = useAuth();
    const [plans, setPlans] = useState([]);
    const [clients, setClients] = useState([]);
    const [techs, setTechs] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({
        client_id: "", nombre: "", sistema: "", norma_iso: "ISO 9001:2015",
        technician_cedula: "", frecuencia_dias: 30,
        proxima_fecha: new Date().toISOString().slice(0, 10), activo: true,
    });
    const canManage = user?.role === "comercial" || user?.role === "supervisor";

    const load = async () => { setPlans((await api.get("/maintenance-plans")).data); };
    useEffect(() => {
        load();
        (async () => {
            const [c, t] = await Promise.all([api.get("/clients"), api.get("/technicians")]);
            setClients(c.data); setTechs(t.data.filter(x => x.activo));
        })();
    }, []);

    const create = async (e) => {
        e.preventDefault();
        await api.post("/maintenance-plans", { ...form, proxima_fecha: new Date(form.proxima_fecha).toISOString() });
        setShowForm(false);
        setForm({ ...form, nombre: "", sistema: "" });
        load();
    };

    const generateOrder = async (plan_id) => {
        if (!window.confirm("¿Generar orden de servicio desde este plan? Avanzará la próxima fecha.")) return;
        try {
            const r = await api.post(`/maintenance-plans/${plan_id}/generate-order`);
            alert(`Orden ${r.data.order_number} creada.`);
            load();
        } catch (err) { alert(err.response?.data?.detail || "Error"); }
    };

    return (
        <div className="max-w-7xl mx-auto p-4 md:p-8" data-testid="plans-page">
            <div className="flex items-end justify-between mb-8 border-b border-zinc-200 pb-6">
                <div>
                    <div className="overline mb-2" style={{ color: "#0F766E" }}>Programa recurrente</div>
                    <h1 className="font-display text-4xl font-black tracking-tighter">Planes de mantenimiento</h1>
                </div>
                {canManage && (
                    <button onClick={() => setShowForm(s => !s)} className="btn-accent flex items-center gap-2" data-testid="toggle-new-plan">
                        <Plus size={16} weight="bold" /> {showForm ? "Cancelar" : "Nuevo plan"}
                    </button>
                )}
            </div>

            {showForm && (
                <form onSubmit={create} className="border border-zinc-200 p-6 bg-zinc-50 mb-8 grid md:grid-cols-2 gap-4" data-testid="new-plan-form">
                    <Field label="Cliente *">
                        <select className="input-atia" value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})} required data-testid="plan-client">
                            <option value="">— Selecciona —</option>
                            {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.razon_social}</option>)}
                        </select>
                    </Field>
                    <Field label="Técnico responsable *">
                        <select className="input-atia" value={form.technician_cedula} onChange={e => setForm({...form, technician_cedula: e.target.value})} required data-testid="plan-tech">
                            <option value="">— Selecciona —</option>
                            {techs.map(t => <option key={t.tech_id} value={t.cedula}>{t.nombre} · CC {t.cedula}</option>)}
                        </select>
                    </Field>
                    <Field label="Nombre del plan *">
                        <input className="input-atia" required value={form.nombre} onChange={e => setForm({...form, nombre: e.target.value})} data-testid="plan-name" placeholder="Mantenimiento preventivo HVAC trimestral" />
                    </Field>
                    <Field label="Sistema a revisar *">
                        <input className="input-atia" required value={form.sistema} onChange={e => setForm({...form, sistema: e.target.value})} data-testid="plan-sistema" />
                    </Field>
                    <Field label="Frecuencia (días) *">
                        <input type="number" min={1} className="input-atia" required value={form.frecuencia_dias} onChange={e => setForm({...form, frecuencia_dias: parseInt(e.target.value)})} data-testid="plan-freq" />
                    </Field>
                    <Field label="Próxima fecha *">
                        <input type="date" className="input-atia" required value={form.proxima_fecha} onChange={e => setForm({...form, proxima_fecha: e.target.value})} data-testid="plan-fecha" />
                    </Field>
                    <Field label="Norma aplicable *">
                        <select className="input-atia" value={form.norma_iso} onChange={e => setForm({...form, norma_iso: e.target.value})} data-testid="plan-norma">
                            {["ISO 9001:2015", "ISO 17020:2012", "ISO 45001:2018", "ISO 14001:2015"].map(n => <option key={n}>{n}</option>)}
                        </select>
                    </Field>
                    <div className="md:col-span-2 flex justify-end">
                        <button type="submit" className="btn-primary" data-testid="submit-plan">Crear plan</button>
                    </div>
                </form>
            )}

            <div className="border border-zinc-200 overflow-x-auto">
                <table className="atia-table" data-testid="plans-table">
                    <thead><tr><th>Plan</th><th>Cliente</th><th>Sistema</th><th>Norma</th><th>Técnico</th><th>Frecuencia</th><th>Próxima visita</th><th>Acción</th></tr></thead>
                    <tbody>
                        {plans.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-zinc-500">Sin planes de mantenimiento.</td></tr>}
                        {plans.map(p => {
                            const fecha = p.proxima_fecha?.slice(0, 10);
                            const isOverdue = new Date(p.proxima_fecha) < new Date();
                            return (
                                <tr key={p.plan_id}>
                                    <td className="font-semibold">{p.nombre}</td>
                                    <td>{p.client?.razon_social || "—"}</td>
                                    <td>{p.sistema}</td>
                                    <td className="text-zinc-600 text-xs">{p.norma_iso}</td>
                                    <td>{p.technician?.nombre || p.technician_cedula}</td>
                                    <td className="font-mono text-xs">{p.frecuencia_dias} días</td>
                                    <td className={`font-mono text-xs ${isOverdue ? "font-bold" : ""}`} style={{ color: isOverdue ? "#EF4444" : undefined }}>{fecha}</td>
                                    <td>
                                        {canManage && (
                                            <button onClick={() => generateOrder(p.plan_id)} className="btn-secondary text-xs flex items-center gap-1" data-testid={`gen-order-${p.plan_id}`}>
                                                <Lightning size={12} weight="bold" /> Generar orden
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return <label className="block"><div className="overline mb-1.5">{label}</div>{children}</label>;
}
